/*
 * Copyright 2025 Intuition Robotics ltd
 */


import * as React from "react";
import {ReactNode} from "react";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {ScaleLinear} from "d3-scale";
import dayjs from "../utils/dayjs";

export type borderBoxCoordinates = {
	value: ReactNode
	placement: number
}

type Props = {
	xScale: ScaleLinear<number, number, any>,
	height: number,
	width?: number
	frequency: number,
	viewBox: number,
	tickLines?: boolean,
	scrollComponent?: () => ReactNode,
	minValue?: number,
	axisPoint?: number,
	tickValues?: ReactNode[],
	borderBoxValues?: { icon?: ReactNode, values: borderBoxCoordinates[] },
	shiftData?: number,
	axisLine?: boolean,
	linesColor?: string,
	alignMiddle?: boolean
}

export class AxisX
	extends BaseComponent<Props, {}> {

	constructor(props: Props) {
		super(props);
	}

	calculateTicksByFreq = () => {
		// if (!this.props.minValue)
		// 	return this.props.xScale.ticks(this.props.viewBox);
		let currentValue = this.props.minValue || 0;
		if (this.props.shiftData)
			currentValue += (this.props.shiftData);
		const tickValues = [];
		for (let i = 0; i <= this.props.viewBox; i++) {
			tickValues.push(currentValue);
			currentValue += this.props.frequency;
		}
		return tickValues;
	};

	getBorderBoxValue = (d: number) => {
		if (!this.props.borderBoxValues)
			return;
		const existingValue = this.props.borderBoxValues.values.find(_value => {
			const valueAtMidnight = dayjs(_value.placement).startOf('day').valueOf();
			const dAtMidnight = dayjs(d).startOf('day').valueOf();
			return valueAtMidnight === dAtMidnight;
		});
		if (!existingValue)
			return;
		return {x: existingValue.placement, y: existingValue.value};
	};

	axisBottom = () => {
		const textPaddingY = !!this.props.borderBoxValues ? 50 : 20;
		return this.calculateTicksByFreq().map((d, i) => (
			<svg className="x-tick" key={i} style={{overflow: 'visible'}}>
				{this.props.tickLines && <line
					style={{stroke: this.props.linesColor || "#e4e5eb"}}
					y1={0}
					y2={this.props.height}
					x1={this.props.xScale(d)}
					x2={this.props.xScale(d)}
				/>}
				{!!this.props.borderBoxValues && this.getBorderBoxValue(d) && <svg style={{overflow: 'visible'}}>
					{/*<text x={this.props.xScale(this.getBorderBoxValue(d)?.x || 0)}*/}
					{/*      y={this.props.height + 18}*/}
					{/*      dominantBaseline={"middle"}*/}
					{/*      textAnchor={"middle"} style={{fill: COLORS.neutral_text_dark_2}}>{this.getBorderBoxValue(d)?.y}</text>*/}
					{/*<foreignObject width="50" height="50" y={this.props.height + 8} x={this.props.xScale(this.getBorderBoxValue(d)?.x || 0) + 25}>*/}
					{/*	{this.getBorderBoxValue(d)?.y}*/}
					{/*</foreignObject>*/}
					<line
						style={{stroke: "#a6aab2"}}
						y1={this.props.height + 35}
						y2={this.props.height + 35}
						x1={-30}
						x2={this.props.width || 200}
					/>
					{this.props.borderBoxValues?.icon &&
					<foreignObject width="50" height="50" y={this.props.height} x={-50}>
						{this.props.borderBoxValues?.icon}
					</foreignObject>}
				</svg>}
				{this.props.scrollComponent && this.props.scrollComponent()}

				{this.props.tickValues &&
				<foreignObject width="60" height="50" y={this.props.height + textPaddingY}
				               x={this.props.alignMiddle ? this.props.xScale(d) - 10 : this.props.xScale(d)}>
					{this.props.tickValues[i]}
				</foreignObject>}
			</svg>
		));
	};

	axisLine = () => {
		const min = this.props.minValue || Math.min(...this.calculateTicksByFreq());
		return <line
			style={{
				stroke: "#a6aab2", strokeWidth: 1
			}}
			y1={0}
			y2={!!this.props.borderBoxValues ? this.props.height + 30 : this.props.height}
			x1={this.props.xScale(this.props.axisPoint || min)}
			x2={this.props.xScale(this.props.axisPoint || min)}
		/>;
	};

	borderBoxValues = () => {
		if (!this.props.borderBoxValues)
			return;
		return this.props.borderBoxValues.values.map((_value,i) => {
			return <foreignObject key={i} width="50" height="50" y={this.props.height + 8} x={this.props.xScale(_value.placement) + 25}>
				{_value.value}
			</foreignObject>;
		});
	};

	render() {
		return <>
			{this.axisBottom()}
			{this.props.axisLine && this.axisLine()}
			{this.borderBoxValues()}
		</>;
	}
}

export default AxisX;
